import { QuestionRenderer } from '@/components/FormPlayer/QuestionRenderer'
import { Button } from '@/components/ui/button'
import {
  type FormBrandSettings,
  type FormLayoutSettings,
  getFormBrandSettings,
  getFormLayoutSettings,
  getFormPlayerStyle,
} from '@/lib/form-theme'
import { cn } from '@/lib/utils'
import { useFormBuilderStore } from '@/stores/formBuilderStore'
import type { AnswerValue, Form, Question } from '@/types/form'
import { CheckCircle2, MonitorSmartphone } from 'lucide-react'
import { useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'

const previewModeCopy = {
  theme: {
    label: '主題預覽',
    title: null,
    description: null,
  },
  font: {
    label: '字體預覽',
    title: '主畫面即時顯示字體節奏',
    description: '標題、描述、答案與按鈕會使用目前選定的標題字體與內文字體。',
  },
  layout: {
    label: '佈局預覽',
    title: '主畫面即時顯示填答版面',
    description: '目前佈局設定會直接呈現在填答預覽中。',
  },
  brand: {
    label: '品牌預覽',
    title: '主畫面即時顯示品牌素材',
    description: 'Logo、Banner 與底部品牌標記會直接在填答畫面中呈現。',
  },
} as const

const fallbackQuestion: Question = {
  id: 'appearance-preview-question',
  formId: 'appearance-preview-form',
  type: 'MULTIPLE_CHOICE',
  title: '你對這次服務體驗滿意嗎？',
  description: '這裡會用目前外觀設定預覽填答者實際看到的畫面。',
  required: false,
  settings: {
    showQuestionNumber: true,
  },
  choices: [
    {
      id: 'appearance-preview-choice-1',
      questionId: 'appearance-preview-question',
      label: '非常滿意',
      value: 'very-satisfied',
      orderIndex: 0,
    },
    {
      id: 'appearance-preview-choice-2',
      questionId: 'appearance-preview-question',
      label: '普通，可以再調整',
      value: 'neutral',
      orderIndex: 1,
    },
  ],
  orderIndex: 0,
}

type AppearanceMainPreviewProps = {
  variant?: 'full' | 'inline'
  title?: string
  description?: string | null
  action?: ReactNode
}

export function AppearanceMainPreview({
  variant = 'full',
  title,
  description,
  action,
}: AppearanceMainPreviewProps = {}) {
  const form = useFormBuilderStore((state) => state.form)
  const questions = useFormBuilderStore((state) => state.questions)
  const selectedQuestionId = useFormBuilderStore((state) => state.selectedQuestionId)
  const appearancePreviewMode = useFormBuilderStore((state) => state.appearancePreviewMode)
  const selectedQuestion =
    questions.find((question) => question.id === selectedQuestionId) ??
    questions[0] ??
    fallbackQuestion
  const question = selectedQuestion.formId === form.id ? selectedQuestion : fallbackQuestion
  const layout = getFormLayoutSettings(form.settings)
  const brand = getFormBrandSettings(form.settings)
  const fillMode = form.settings.fillMode === 'step' ? 'step' : 'one_page'
  const copy = previewModeCopy[appearancePreviewMode]
  const answerKey = `${question.id}:${appearancePreviewMode}:${variant}`
  const [answerState, setAnswerState] = useState<{
    key: string
    value: AnswerValue | undefined
  }>({ key: answerKey, value: undefined })
  const answer = answerState.key === answerKey ? answerState.value : undefined
  const showProgress = form.settings.showProgress !== false
  const previewFrame = (
    <div
      className={cn(
        'form-player-theme relative overflow-hidden border shadow-xl',
        variant === 'full' ? 'min-h-[680px]' : 'min-h-[520px]',
      )}
      style={{
        ...getFormPlayerStyle(form.themeConfig, form.settings),
        borderColor: 'color-mix(in srgb, var(--form-accent,#6366F1) 20%, transparent)',
        borderRadius: 'calc(var(--form-radius,8px) + 12px)',
      }}
    >
      <PreviewBrandLogo brand={brand} />
      {showProgress && layout.progressPosition === 'top' && (
        <PreviewProgressBar position="top" style={form.themeConfig.progressStyle} />
      )}

      <div
        className={cn(
          'mx-auto flex w-full flex-col',
          variant === 'full' ? 'min-h-[680px] px-8 py-16' : 'min-h-[520px] px-6 py-14',
        )}
        style={{
          alignItems: getAlignItems(layout.questionAlign),
          justifyContent: layout.verticalAlign === 'top' ? 'flex-start' : 'center',
          maxWidth: 'var(--form-max-width,720px)',
          textAlign: layout.questionAlign,
        }}
      >
        <div className="w-full">
          <PreviewBrandBanner brand={brand} surface="questions" />
          {layout.showQuestionNumber && (
            <div
              className="mb-4 font-medium"
              style={{
                color: 'var(--form-answer,#64748B)',
                fontFamily: 'var(--form-body-font,var(--form-font,Geist))',
                fontSize: 'var(--form-meta-size,0.9375rem)',
              }}
            >
              1 / {Math.max(questions.length, 1)}
            </div>
          )}
          <h1
            className="font-semibold tracking-normal"
            style={{
              color: 'var(--form-text,#0F172A)',
              fontFamily: 'var(--form-heading-font,var(--form-font,Geist))',
              fontSize: 'var(--form-question-size,2.25rem)',
              lineHeight: 'calc(var(--form-line-height,1.55) - 0.28)',
            }}
          >
            {question.title}
          </h1>
          {question.description && (
            <p
              className="mt-3"
              style={{
                color: 'var(--form-answer,#475569)',
                fontFamily: 'var(--form-body-font,var(--form-font,Geist))',
                fontSize: 'var(--form-body-size,1.125rem)',
                lineHeight: 'var(--form-line-height,1.55)',
              }}
            >
              {question.description}
            </p>
          )}
          <div style={{ marginTop: 'var(--form-question-block-gap,2rem)' }}>
            <QuestionRenderer
              question={question}
              value={answer}
              onChange={(value) => setAnswerState({ key: answerKey, value })}
              onSubmit={() => undefined}
              autoFocus={false}
              theme={form.themeConfig}
            />
          </div>
          {fillMode === 'step' ? <PreviewNavigation layout={layout} /> : null}
        </div>
      </div>

      {showProgress && layout.progressPosition === 'bottom' && (
        <PreviewProgressBar position="bottom" style={form.themeConfig.progressStyle} />
      )}
      <PreviewBrandMark brand={brand} layout={layout} />
    </div>
  )

  if (variant === 'inline') {
    return (
      <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 rounded-full border border-indigo-100 bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700">
              <MonitorSmartphone className="size-3.5" />
              {copy.label}
            </div>
            <h3 className="mt-2 text-base font-semibold text-slate-950">
              {title ?? '外觀即時預覽'}
            </h3>
            {description !== null ? (
              <p className="mt-1 text-sm text-slate-500">
                {description ?? '目前外觀設定會同步套用到這個填答頁預覽。'}
              </p>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            {action}
            <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-medium text-slate-600">
              <CheckCircle2 className="size-4 text-emerald-600" />
              即時同步
            </div>
          </div>
        </div>
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-slate-100 p-4">
          {previewFrame}
        </div>
      </section>
    )
  }

  return (
    <div className="flex min-h-full flex-col bg-slate-100 p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="inline-flex items-center gap-2 rounded-full border border-indigo-100 bg-white px-3 py-1 text-xs font-medium text-indigo-700 shadow-sm">
            <MonitorSmartphone className="size-3.5" />
            {copy.label}
          </div>
          {copy.title ? (
            <h2 className="mt-2 text-lg font-semibold text-slate-950">{copy.title}</h2>
          ) : null}
          {copy.description ? (
            <p className="mt-1 text-sm text-slate-500">{copy.description}</p>
          ) : null}
        </div>
        {action ? <div className="flex items-center gap-2">{action}</div> : null}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mx-auto w-full max-w-5xl">{previewFrame}</div>
      </div>
    </div>
  )
}

function PreviewBrandLogo({ brand }: { brand: FormBrandSettings }) {
  if (!brand.showLogo) return null

  const content = (
    <div
      className={cn(
        'flex max-w-[min(80%,360px)] items-center gap-2.5 px-2.5 py-2 text-sm',
        brand.logoFrame && 'border shadow-sm backdrop-blur',
      )}
      style={{
        background: brand.logoFrame ? 'color-mix(in srgb, var(--form-bg,#F8FAFC) 82%, white)' : '',
        borderColor: brand.logoFrame
          ? 'color-mix(in srgb, var(--form-accent,#6366F1) 24%, transparent)'
          : '',
        borderRadius: brand.logoFrame ? 'calc(var(--form-radius,8px) + 8px)' : undefined,
        color: 'var(--form-text,#0F172A)',
        fontFamily: 'var(--form-body-font,var(--form-font,Geist))',
      }}
    >
      {brand.logoUrl ? (
        <img
          src={brand.logoUrl}
          alt={brand.logoAlt}
          className={cn(
            'shrink-0 object-contain',
            brand.logoShape === 'circle' && 'rounded-full',
            brand.logoShape === 'rounded' && 'rounded-md',
          )}
          style={{
            height: Math.min(brand.logoSize, 72),
            maxWidth: Math.min(brand.logoSize * 3.2, 220),
            width: 'auto',
          }}
        />
      ) : (
        <span
          className={cn(
            'grid shrink-0 place-items-center font-semibold',
            brand.logoShape === 'circle' && 'rounded-full',
            brand.logoShape === 'rounded' && 'rounded-md',
          )}
          style={{
            background: 'var(--form-accent,#6366F1)',
            color: 'var(--form-button-text,#FFFFFF)',
            fontSize: Math.max(12, Math.round(Math.min(brand.logoSize, 72) * 0.34)),
            height: Math.min(brand.logoSize, 72),
            width: Math.min(brand.logoSize, 72),
          }}
        >
          {brand.logoText}
        </span>
      )}
      {brand.showBrandName && (
        <span className="min-w-0 truncate font-semibold tracking-normal">{brand.brandName}</span>
      )}
    </div>
  )

  return (
    <div
      className={cn(
        'absolute top-5 right-5 left-5 z-20 flex',
        brand.logoPosition === 'top-center' && 'justify-center',
        brand.logoPosition === 'top-right' && 'justify-end',
        brand.logoPosition === 'top-left' && 'justify-start',
      )}
    >
      {content}
    </div>
  )
}

function PreviewBrandBanner({
  brand,
  surface,
}: {
  brand: FormBrandSettings
  surface: 'welcome' | 'questions' | 'thankyou'
}) {
  const shouldShow =
    brand.showBanner &&
    brand.bannerUrl &&
    (brand.bannerDisplay === 'all' ||
      brand.bannerDisplay === surface ||
      (brand.bannerDisplay === 'questions' && surface === 'questions'))

  if (!shouldShow) return null

  return (
    <div
      className="mb-8 w-full overflow-hidden border shadow-sm"
      style={{
        background: 'color-mix(in srgb, var(--form-bg,#F8FAFC) 78%, white)',
        borderColor: 'color-mix(in srgb, var(--form-accent,#6366F1) 22%, transparent)',
        borderRadius: brand.bannerRadius,
        height: brand.bannerHeight,
      }}
    >
      <div
        className="relative size-full overflow-hidden"
        style={{ borderRadius: brand.bannerRadius }}
      >
        <img
          src={brand.bannerUrl}
          alt={brand.bannerAlt}
          className="size-full"
          style={{
            objectFit: brand.bannerFit,
            objectPosition: `${brand.bannerFocalX}% ${brand.bannerFocalY}%`,
            transform: `scale(${brand.bannerZoom})`,
            transformOrigin: `${brand.bannerFocalX}% ${brand.bannerFocalY}%`,
          }}
        />
        {brand.bannerOverlayOpacity > 0 && (
          <div
            className="absolute inset-0"
            style={{
              background: brand.bannerOverlayColor,
              opacity: brand.bannerOverlayOpacity / 100,
            }}
          />
        )}
      </div>
    </div>
  )
}

function PreviewNavigation({ layout }: { layout: FormLayoutSettings }) {
  return (
    <div
      className={cn(
        'mt-8 flex w-full gap-3',
        layout.navigationLayout === 'stacked'
          ? 'flex-col'
          : layout.navigationLayout === 'split'
            ? 'justify-between'
            : layout.questionAlign === 'center'
              ? 'justify-center'
              : layout.questionAlign === 'right'
                ? 'justify-end'
                : 'justify-start',
      )}
    >
      {layout.showBackButton && (
        <Button
          type="button"
          variant="outline"
          className="border"
          style={
            {
              background: 'color-mix(in srgb, var(--form-bg,#F8FAFC) 82%, white)',
              borderColor: 'color-mix(in srgb, var(--form-accent,#6366F1) 32%, transparent)',
              borderRadius: 'var(--form-radius,8px)',
              color: 'var(--form-text,#0F172A)',
              fontFamily: 'var(--form-body-font,var(--form-font,Geist))',
            } as CSSProperties
          }
        >
          返回
        </Button>
      )}
      <Button
        type="button"
        style={
          {
            background: 'var(--form-button,#6366F1)',
            borderRadius: 'var(--form-radius,8px)',
            color: 'var(--form-button-text,#FFFFFF)',
            fontFamily: 'var(--form-body-font,var(--form-font,Geist))',
          } as CSSProperties
        }
      >
        繼續
      </Button>
    </div>
  )
}

function PreviewProgressBar({
  position,
  style,
}: {
  position: FormLayoutSettings['progressPosition']
  style: Form['themeConfig']['progressStyle']
}) {
  if (style === 'text') {
    return (
      <div
        className={cn(
          'absolute right-5 z-20 rounded-full px-3 py-1.5 text-xs font-medium shadow-sm',
          position === 'top' ? 'top-5' : 'bottom-5',
        )}
        style={{
          background: 'color-mix(in srgb, var(--form-bg,#F8FAFC) 88%, white)',
          color: 'var(--form-answer,#64748B)',
        }}
      >
        33%
      </div>
    )
  }

  return (
    <div
      className={cn(
        'absolute right-0 left-0 z-20',
        style === 'thin' ? 'h-1' : 'h-2',
        position === 'top' ? 'top-0' : 'bottom-0',
      )}
      style={{
        background: 'color-mix(in srgb, var(--form-accent,#6366F1) 14%, transparent)',
      }}
    >
      <div
        className="h-full"
        style={{
          background: 'var(--form-accent,#6366F1)',
          width: '33%',
        }}
      />
    </div>
  )
}

function PreviewBrandMark({
  brand,
  layout,
}: {
  brand: FormBrandSettings
  layout: FormLayoutSettings
}) {
  if (!brand.showBrandMark) return null

  const isSolid = brand.brandMarkStyle === 'solid'

  return (
    <div
      className={cn(
        'absolute right-5 left-5 z-20 flex',
        layout.progressPosition === 'bottom' ? 'bottom-8' : 'bottom-5',
        brand.brandMarkPosition === 'bottom-center' && 'justify-center',
        brand.brandMarkPosition === 'bottom-right' && 'justify-end',
        brand.brandMarkPosition === 'bottom-left' && 'justify-start',
      )}
    >
      <span
        className={cn(
          'inline-flex min-h-8 items-center rounded-full px-3 py-1.5 text-xs font-medium',
          brand.brandMarkStyle !== 'subtle' && 'border backdrop-blur',
        )}
        style={{
          background:
            brand.brandMarkStyle === 'subtle'
              ? 'transparent'
              : isSolid
                ? 'var(--form-button,#6366F1)'
                : 'color-mix(in srgb, var(--form-bg,#F8FAFC) 82%, white)',
          borderColor:
            brand.brandMarkStyle === 'subtle'
              ? 'transparent'
              : 'color-mix(in srgb, var(--form-accent,#6366F1) 24%, transparent)',
          color: isSolid ? 'var(--form-button-text,#FFFFFF)' : 'var(--form-answer,#64748B)',
          fontFamily: 'var(--form-body-font,var(--form-font,Geist))',
          opacity: brand.brandMarkOpacity / 100,
        }}
      >
        {brand.brandMarkText}
      </span>
    </div>
  )
}

function getAlignItems(questionAlign: FormLayoutSettings['questionAlign']) {
  if (questionAlign === 'center') return 'center'
  if (questionAlign === 'right') return 'flex-end'
  return 'stretch'
}
